import { useEffect, useRef } from 'react';

const AnimatedBackground = ({ darkMode }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    let animationFrameId;
    let time = 0;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    const theme = darkMode
      ? {
          background: '#050816',
          particles: [
            'rgba(222, 2, 20, 0.56)',
            'rgba(250, 250, 250, 0.3)',
            'rgba(255, 107, 0, 0.7)',
          ],
          wave: 'rgba(37, 99, 235, 0.08)',
          spiral: 'rgba(124, 58, 237, 0.12)',
          orbit: 'rgba(255, 107, 0, 0.05)',
          glow: 20,
          particleCount: 120,
        }
      : {
          background: '#F8FAFC',
          particles: [
            'rgba(222, 2, 20, 0.56)',
            'rgba(253, 232, 16, 0.3)',
            'rgba(255, 107, 0, 0.35)',
          ],
          wave: 'rgba(37, 99, 235, 0.09)',
          spiral: 'rgba(124, 58, 237, 0.10)',
          orbit: 'rgba(255, 107, 0, 0.08)',
          glow: 14,
          particleCount: 90,
        };

    class Particle {
      constructor() {
        this.reset();
      }

      reset() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.size = darkMode
          ? Math.random() * 2.5 + 1
          : Math.random() * 3 + 1;
        this.speedX = (Math.random() - 0.5) * 0.4;
        this.speedY = (Math.random() - 0.5) * 0.4;
        this.color =
          theme.particles[
            Math.floor(Math.random() * theme.particles.length)
          ];
      }

      update() {
        this.x += this.speedX;
        this.y += this.speedY;

        if (this.x < 0 || this.x > canvas.width) {
          this.speedX *= -1;
        }

        if (this.y < 0 || this.y > canvas.height) {
          this.speedY *= -1;
        }
      }

      draw() {
        ctx.beginPath();
        ctx.shadowBlur = darkMode ? theme.glow : theme.glow * 1.4;
        ctx.shadowColor = this.color;
        ctx.fillStyle = this.color;
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 0;
      }
    }

    let particles = [];

    const initParticles = () => {
      particles = [];
      for (let i = 0; i < theme.particleCount; i++) {
        particles.push(new Particle());
      }
    };

    const drawWaves = () => {
      for (let i = 0; i < 4; i++) {
        ctx.beginPath();
        const baseY = canvas.height * (0.2 + i * 0.2);
        for (let x = 0; x < canvas.width; x += 8) {
          const y =
            baseY +
            Math.sin(x * 0.004 + time * 0.01 + i) * 30 +
            Math.cos(x * 0.002 + time * 0.008) * 15;
          if (x === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.strokeStyle = theme.wave;
        ctx.lineWidth = darkMode ? 2 : 1.8;
        ctx.shadowBlur = darkMode ? 12 : 4;
        ctx.shadowColor = theme.wave;
        ctx.stroke();
        ctx.shadowBlur = 0;
      }
    };

    const drawSpiral = () => {
      const cx = canvas.width / 2;
      const cy = canvas.height / 2;
      ctx.beginPath();
      for (let a = 0; a < Math.PI * 8; a += 0.05) {
        const r = a * 12;
        const x = cx + Math.cos(a + time * 0.01) * r;
        const y = cy + Math.sin(a + time * 0.008) * r;
        if (a === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.strokeStyle = theme.spiral;
      ctx.lineWidth = darkMode ? 1.5 : 1;
      ctx.shadowBlur = darkMode ? 18 : 5;
      ctx.shadowColor = theme.spiral;
      ctx.stroke();
      ctx.shadowBlur = 0;
    };

    const drawOrbits = () => {
      const cx = canvas.width / 2;
      const cy = canvas.height / 2;
      for (let i = 0; i < 5; i++) {
        ctx.beginPath();
        const radius = 120 + i * 60;
        for (let a = 0; a < Math.PI * 2; a += 0.05) {
          const x = cx + Math.cos(a + time * 0.003 * (i + 1)) * radius;
          const y = cy + Math.sin(a + time * 0.002 * (i + 1)) * radius * 0.6;
          if (a === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.strokeStyle = theme.orbit;
        ctx.lineWidth = 1;
        ctx.stroke();
      }
    };

    const animate = () => {
      time++;
      if (darkMode) {
        ctx.fillStyle = theme.background;
      } else {
        ctx.fillStyle = 'rgba(248, 250, 252, 0.92)';
      }
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      drawOrbits();
      drawWaves();
      drawSpiral();
      particles.forEach((p) => {
        p.update();
        p.draw();
      });
      animationFrameId = requestAnimationFrame(animate);
    };

    resizeCanvas();
    initParticles();
    animate();

    window.addEventListener('resize', resizeCanvas);

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', resizeCanvas);
    };
  }, [darkMode]);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 w-full h-full pointer-events-none"
    />
  );
};

export default AnimatedBackground;